import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPermissionsData,
  fetchRolePermissions,
  updateRolePermissions,
} from "../Thunks/permissionsThunk";
import {
  addAsyncResourceThunk,
  completeResourceSuccess,
  applyResourceRejected,
  applySaveRejected,
} from "../helpers/asyncResource";
import {
  normalizePermissionsCategories,
  normalizeRolePermissionIds,
  mapUpdateRolePermissionsResponse,
} from "../mappers/permissionsMappers";

const initialState = {
  categories: [],
  rolePermissions: {},
  loading: false,
  error: null,
  saving: false,
  saveError: null,
};

export const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    clearPermissions: (state) => {
      state.categories = [];
      state.rolePermissions = {};
      state.error = null;
    },
    // Update role permissions locally (for optimistic updates)
    setRolePermissionsLocally: (state, action) => {
      const { role, permissions } = action.payload;
      state.rolePermissions[role] = permissions;
    },
    // Toggle a single permission for a role locally
    togglePermissionLocally: (state, action) => {
      const { role, permissionId } = action.payload;
      const rolePerms = state.rolePermissions[role] || [];
      const hasPermission = rolePerms.includes(permissionId);

      if (hasPermission) {
        state.rolePermissions[role] = rolePerms.filter((p) => p !== permissionId);
      } else {
        state.rolePermissions[role] = [...rolePerms, permissionId];
      }
    },
    // Toggle all permissions in a category for a role locally
    toggleCategoryLocally: (state, action) => {
      const { role, categoryPermissionIds, enable } = action.payload;
      const rolePerms = state.rolePermissions[role] || [];

      if (enable) {
        // Add all permissions from this category
        const newPermissions = [...rolePerms];
        categoryPermissionIds.forEach((id) => {
          if (!newPermissions.includes(id)) {
            newPermissions.push(id);
          }
        });
        state.rolePermissions[role] = newPermissions;
      } else {
        // Remove all permissions from this category
        state.rolePermissions[role] = rolePerms.filter((p) => !categoryPermissionIds.includes(p));
      }
    },
  },
  extraReducers: (builder) => {
    addAsyncResourceThunk(builder, fetchPermissionsData, {
      onFulfilled: (state, action) => {
        state.categories = normalizePermissionsCategories(action.payload) || [];
        // Do not clear rolePermissions — fetchPermissionsData can finish after fetchRolePermissions
        // and would wipe the matrix; PermissionTab refetches per role when selection changes.
      },
    });
    builder
      .addCase(fetchRolePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        completeResourceSuccess(state);
        const roleId = action.meta.arg;
        state.rolePermissions[roleId] = normalizeRolePermissionIds(action.payload);
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        applyResourceRejected(state, action, "Failed to fetch role permissions");
      })
      .addCase(updateRolePermissions.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateRolePermissions.fulfilled, (state, action) => {
        state.saving = false;
        state.saveError = null;
        const { roleId, permissions } = action.meta.arg;
        const fallbackIds = Array.isArray(permissions) ? permissions : [];
        const nextIds = mapUpdateRolePermissionsResponse(action.payload, fallbackIds);
        if (roleId) {
          state.rolePermissions[roleId] = nextIds;
        }
      })
      .addCase(updateRolePermissions.rejected, (state, action) => {
        applySaveRejected(state, action, "Failed to update role permissions");
      });
  },
});

export const {
  clearPermissions,
  setRolePermissionsLocally,
  togglePermissionLocally,
  toggleCategoryLocally,
} = permissionsSlice.actions;

// Selectors
export const selectPermissionCategories = (state) => state.permissions.categories;
export const selectRolePermissions = (state) => state.permissions.rolePermissions;
export const selectPermissionsLoading = (state) => state.permissions.loading;
export const selectPermissionsError = (state) => state.permissions.error;
export const selectPermissionsSaving = (state) => state.permissions.saving;
export const selectPermissionsSaveError = (state) => state.permissions.saveError;

// Get permissions for a specific role
export const selectPermissionsForRole = (role) => (state) =>
  state.permissions.rolePermissions[role] || [];

export default permissionsSlice.reducer;
